import posthog from 'posthog-js'
import { resolvePosthogToken } from '@repo/shared/analytics'
import { StrictMode } from 'react'
import * as Sentry from '@sentry/react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './styles/globals.css'
import './components/form-builder/FormBuilder.css'
// Import the generated route tree
import { routeTree } from './routeTree.gen'

// After a new deployment, old JS chunks no longer exist on the server.
// Vite fires this event when a dynamic import (code-split route) fails to load.
// Force a full page reload so the browser fetches the new entry point and chunks.
window.addEventListener('vite:preloadError', (_event) => {
	window.location.reload()
})

async function initAnalytics() {
	const token = await resolvePosthogToken(
		import.meta.env.VITE_POSTHOG_TOKEN,
		import.meta.env.VITE_POSTHOG_TOKEN_STAGING,
	)
	if (token) {
		const host = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com'
		posthog.init(token, {
			api_host: host,
			capture_pageview: 'history_change',
			capture_pageleave: 'if_capture_pageview',
			capture_dead_clicks: true,
			rageclick: true,
			autocapture: {
				url_allowlist: ['/tasks/[0-9a-f]{8}-'],
			},
			on_request_error: (err) => {
				console.error('[analytics] posthog request error', err)
			},
			session_recording: {
				maskAllInputs: true,
				maskInputOptions: { password: true },
			},
		})
		window.posthog = posthog
	}
}
initAnalytics()

// Create a new router instance
const router = createRouter({ routeTree })

// Initialize Sentry after router is created so we can pass it to the tracing integration
Sentry.init({
	dsn: 'https://b520ba17514a6aca5ea31eeedc41281f@o1315096.ingest.sentry.io/4505829570969600',
	integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
	tracesSampleRate: 0.2,
	enabled: import.meta.env.PROD,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router
	}
}

// Create a QueryClient instance
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60 * 5, // 5 minutes
			refetchOnWindowFocus: false,
		},
	},
})

// Render the app
const rootElement = document.getElementById('root')!
if (rootElement.hasChildNodes()) {
	rootElement.innerHTML = ''
}
const root = ReactDOM.createRoot(rootElement)
root.render(
	<StrictMode>
		<Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
			<QueryClientProvider client={queryClient}>
				<RouterProvider router={router} />
			</QueryClientProvider>
		</Sentry.ErrorBoundary>
	</StrictMode>,
)

